import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import {
  isStripeConnectedSelector,
  isStripeChargesEnabledSelector,
  errorStripeMessage,
  isPaypalConnectedSelector
} from "store/user"

/**
 * Centralizes the payment-provider "problem" state that blocks booking creation and
 * website-integration actions, surfacing the same message shown on the
 * Settings > Payment providers card (StripeDetail.jsx).
 *
 * A booking is blocked only when NO payment provider is usable, i.e. Stripe cannot
 * charge AND PayPal is not connected. A connected & healthy PayPal is enough on its
 * own, so a Stripe error / under-review state does NOT block when PayPal works.
 *
 * When blocked, the message explains the most relevant reason:
 *   - Stripe reports an error       -> that error message
 *   - Stripe is under review        -> "Your account is under review"
 *   - Nothing connected at all      -> "Please connect your payment provider"
 *
 * Also exposes Stripe-specific state (independent of PayPal) so payment UIs can
 * disable the Stripe option on its own when Stripe can't charge:
 *   - isStripeChargeable  : Stripe is connected, charges enabled, no error
 *   - stripeProblemMessage: the Stripe error / under-review message, if any
 *
 * @returns {{ hasStripeIssue: boolean, stripeIssueMessage: string, isStripeChargeable: boolean, stripeProblemMessage: string }}
 */
export const useStripeIssue = () => {
  const { t } = useTranslation()
  const isStripeConnected = useSelector(isStripeConnectedSelector)
  const isStripeChargeEnabled = useSelector(isStripeChargesEnabledSelector)
  const stripeErrorMessage = useSelector(errorStripeMessage)
  const isPaypalConnected = useSelector(isPaypalConnectedSelector)

  const isStripeChargeable = isStripeConnected && isStripeChargeEnabled && !stripeErrorMessage
  const isUnderReview = isStripeConnected && !isStripeChargeEnabled && !stripeErrorMessage

  // Stripe-specific problem, regardless of whether PayPal can cover the booking.
  let stripeProblemMessage = ""
  if (stripeErrorMessage) {
    stripeProblemMessage = stripeErrorMessage
  } else if (isUnderReview) {
    stripeProblemMessage = t("pro.settings.stripe.account_under_review")
  }

  // Booking is blocked only when there is no usable way to charge the customer.
  const hasStripeIssue = !isStripeChargeable && !isPaypalConnected
  const stripeIssueMessage = hasStripeIssue ? stripeProblemMessage || t("form_validation.payment_provider_missing") : ""

  return { hasStripeIssue, stripeIssueMessage, isStripeChargeable, stripeProblemMessage }
}
